import AdminUser from '../models/admin_user';
import { components } from '../schema/docs/api/admin';
import { throwOnError } from './utils';

type Clearing = components['schemas']['Clearing'];
type ClearingShowResponse = components['schemas']['ClearingShowResponse'];
type ClearingReceivableSummary = components['schemas']['ClearingReceivableSummary'];

type GetClearingResponse = ClearingShowResponse;
type GetClearingsResponse = Clearing[];

type Paginated<T> = {
  rows: T[];
  count: number;
  next: string | null;
  prev: string | null;
  per_page: number;
};

type ClearingTask = {
  id: string;
  status: 'submitted' | 'processing' | 'error' | 'done';
  closing_date: string;
  done_at?: string;
  created_at: string;
  updated_at: string;
  operator: AdminUser;
  count?: number;
};

type ClearingSetting = {
  monthly_settlement_dates: number[] | null;
  auto_offset_enabled?: boolean;
};

/* eslint-disable no-underscore-dangle */
export const getClearingSummary = (privateMoneyId: string, query = '') =>
  fetch(`/api/private-moneys/${privateMoneyId}/summary?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError);

export const previewClearings = (query = '') =>
  fetch(`/api/clearings/preview?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json();
  });

export const createClearings = (postData: Record<string, unknown> = {}) =>
  fetch('/api/clearings', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json();
  });

export const getClearings = (id = '', query = '') =>
  fetch(`/api/private-moneys/${id}/clearings?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError<GetClearingsResponse>);

export const getClearing = (id: string) =>
  fetch(`/api/clearings/${id}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError<GetClearingResponse>);

export const updateClearings = (id: string, postData: Record<string, unknown> = {}) =>
  fetch(`/api/clearings/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json();
  });

export const getFlicoData = (query = '') =>
  fetch(`/api/clearings/flico?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/octet-stream',
    },
  }).then((response) => {
    return response;
  });

export const getClearingTasks = (query = '') =>
  fetch(`/api/clearings/tasks?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<Paginated<ClearingTask>>);

export const createClearingTasks = (params: { private_money_id: string; closing_date: string }) =>
  fetch('/api/clearings/tasks', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ ...params, _csrf_token: window._csrf_token }),
  }).then(throwOnError<ClearingTask>);

export const getClearingAutomationSetting = (privateMoneyId: string) =>
  fetch(`/api/private-moneys/${privateMoneyId}/clearing-setting`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError<ClearingSetting>);

export const createClearingAutomationSetting = (
  privateMoneyId: string,
  postData: Record<string, unknown>
) =>
  fetch(`/api/private-moneys/${privateMoneyId}/clearing-setting`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token, ...postData }),
  }).then(throwOnError<ClearingSetting>);

export const updateClearingAutomationSetting = (
  privateMoneyId: string,
  postData: Record<string, unknown>
) =>
  fetch(`/api/private-moneys/${privateMoneyId}/clearing-setting`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token, ...postData }),
  }).then(throwOnError<ClearingSetting>);

export const deleteClearingAutomationSetting = (privateMoneyId: string) =>
  fetch(`/api/private-moneys/${privateMoneyId}/clearing-setting`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then(throwOnError<ClearingSetting>);

// 相殺除外: 確定前（payment_clearing.done_at が null）の clearing-offset を削除する。
// 確定済みは 422。理由は永続化しない方針のため reason クエリは送らない。
export const deleteClearingOffset = (offsetId: string) =>
  fetch(`/api/clearing-offsets/${offsetId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json();
  });

// 未回収債権残高・件数のサーバ集計（ページング非依存）。
export const getClearingReceivableSummary = (privateMoneyId: string, query = '') =>
  fetch(`/api/private-moneys/${privateMoneyId}/clearings/receivable-summary?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError<ClearingReceivableSummary>);

// 一括精算: 複数の精算データを一括で精算済(done_at)にする。最大1000件/リクエスト
// （超過分は呼び出し側でチャンク分割して逐次呼ぶ）。既に精算済の id はスキップ（冪等）。
// 発行体のみ。支払の精算済化は確定相殺で残高0になった請求も連動して done にする（カスケード）。
export type BulkSettleResult = {
  settled: number;
  cascade_resolved: number;
  skipped: number;
};

export const bulkSettleClearings = (clearingIds: string[]) =>
  fetch('/api/clearings/bulk-settle', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token, clearing_ids: clearingIds }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json() as Promise<BulkSettleResult>;
  });

// 手動 債権相殺付与: 支払 clearing に同一加盟店の未回収請求(債権)を1件相殺充当する。
// amount 省略時はサーバ側で min(充当可能額, 残債) を充当する。
export const createClearingOffset = (postData: {
  payment_clearing_id: string;
  invoice_clearing_id: string;
  amount?: number;
}) =>
  fetch('/api/clearing-offsets', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token, ...postData }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json();
  });

// ---------------------------------------------------------------------------
// 振込データ(全銀ファイル)
//
// 型は schema/docs/api/admin.ts から取らず手書きしている。生成物が openapi-typescript v6 で
// 作られており、現在の npx が引く v7 で作り直すとファイル全体(2万行超)が書き換わって
// この変更に無関係な差分になるため。同ファイル内の ClearingTask などと同じ扱い。
// ---------------------------------------------------------------------------

export type ZenginTransferBreakdown = {
  clearing_id: string;
  private_money?: { id: string; name: string };
  from: string;
  to: string;
  amount: number;
};

export type ZenginTransfer = {
  // 集約キー。口座は属性にすぎず、同一口座を複数の加盟事業者が登録しているケースがある。
  claimed_organization: { code: string; name: string };
  bank_code: string;
  bank_name: string;
  bank_branch_code: string;
  bank_branch_name: string;
  bank_account_type: string;
  bank_account: string;
  bank_account_holder_name: string;
  amount: number;
  breakdown: ZenginTransferBreakdown[];
};

export type ZenginExcluded = {
  clearing_id: string;
  claimed_organization: { code: string; name: string };
  reason: 'bank_account_unregistered';
};

export type ZenginWarning = {
  type: 'bank_master_unresolved' | 'field_sanitized' | 'unsettled_in_other_batch';
  organization_code?: string;
  organization_name?: string;
  field?: string;
  bank_code?: string;
  clearing_id?: string;
};

export type ZenginExportBatch = {
  id: string;
  created_at: string;
  scheduled_transfer_date: string;
  transfer_count: number;
  total_amount: number;
  organization: { code: string; name: string };
  operator: { id: string; email: string };
  clearing_count?: number;
  unsettled_count?: number;
  url?: string;
  discarded_at?: string | null;
  discarded_by?: { id: string; email: string };
};

export type ZenginFileResult = {
  // この結果の内容を表す指紋。ダウンロード時に渡すと、プレビューで確認した内容から
  // 対象が変わっていないことをサーバが検証する。
  preview_token: string;
  url?: string;
  export_batch?: ZenginExportBatch;
  count: number;
  total_amount: number;
  transfers: ZenginTransfer[];
  excluded: ZenginExcluded[];
  warnings: ZenginWarning[];
};

// 複数マネーの精算を加盟事業者単位に合算した全銀ファイルを作る。
// dry_run のときはファイルも出力バッチも作らず、同じ経路で組み立てた結果だけを返す
// (プレビューと実出力が乖離しない)。
export const createClearingZenginFile = (postData: {
  private_money_ids: string[];
  scheduled_transfer_date: string;
  closing_date_from?: string;
  closing_date_to?: string;
  claimed_organization_code?: string;
  dry_run?: boolean;
  preview_token?: string;
}) =>
  fetch('/api/clearings/zengin-file', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: window._csrf_token, ...postData }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json() as Promise<ZenginFileResult>;
  });

export type ZenginExportBatchClearing = {
  id: string;
  claimed_organization: { code: string; name: string };
  private_money: { id: string; name: string };
  from: string;
  to: string;
  payment_amount: number;
  invoice_amount: number;
  clearing_type: 'payment' | 'invoice';
  // 相殺前の精算額(支払 − 請求)
  pre_offset_amount: number;
  // 債権相殺額
  offset_amount: number;
  // 相殺後の実振込額。バッチの振込金額はこの合計。請求行では null。
  actual_transfer_amount: number | null;
  done_at: string | null;
};

export type ZenginExportBatchDetail = ZenginExportBatch & {
  clearings: ZenginExportBatchClearing[];
};

export type SettleExportBatchResult = {
  settled: number;
  cascade_resolved: number;
  skipped: number;
};

export const getClearingExportBatches = (query = '') =>
  fetch(`/api/clearings/export-batches?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
  }).then(throwOnError) as Promise<{ export_batches: ZenginExportBatch[] }>;

export const getClearingExportBatch = (id: string) =>
  fetch(`/api/clearings/export-batches/${id}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
  }).then(throwOnError) as Promise<ZenginExportBatchDetail>;

export const getClearingExportBatchFile = (id: string) =>
  fetch(`/api/clearings/export-batches/${id}/file`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
  }).then(throwOnError) as Promise<{ url: string }>;

// バッチ単位の精算。精算対象はサーバがバッチから解決するため id は渡さない
// (「ファイルに入った集合」以外を触れないようにするための設計)。
export const settleClearingExportBatch = (id: string) =>
  fetch(`/api/clearings/export-batches/${id}/settle`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json() as Promise<SettleExportBatchResult>;
  });

// 破棄 = 「出力したが銀行に上げていない」の記録。取り消しは DELETE。
export const discardClearingExportBatch = (id: string, discard = true) =>
  fetch(`/api/clearings/export-batches/${id}/discard`, {
    method: discard ? 'POST' : 'DELETE',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json() as Promise<ZenginExportBatch>;
  });

// 精算取消(部分返戻の戻し)。一括版は無い。
export const unsettleClearing = (id: string) =>
  fetch(`/api/clearings/${id}/unsettle`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ _csrf_token: window._csrf_token }),
  }).then((response) => {
    if (!response.ok) {
      throw response.json();
    }
    return response.json() as Promise<{ reverted_count: number }>;
  });
